<template>
  <div class="page-content" id="plans">
    <Titlebar :incrustation="'fa-map'">
      <h1>{{ $t("plans.titles.manager") }}</h1>
    </Titlebar>
    <div class="row" style="height: calc(100vh - 78px - 100px)">
      <LoadingMini ref="loading"></LoadingMini>
      <div class="col-4 full-height">
        <UiCard class="full-height" style="overflow: auto">
          <tree
            ref="tree"
            :treeData="treeData"
            :options="options"
            @treeNodeClick="handleNode"
            :icons="icons"
            @treeNodeRightClick="handleRightClick"
            :selected="externalSelected"
            @endDrag="handleSort"
            :root="rootNode"
          />
        </UiCard>
      </div>
      <div class="col-8 full-height">
        <UiCard
          class="full-height relative"
          style="padding: 0 !important; overflow: hidden"
        >
          <div class="plans-logo">
            <UiBrandLogoWide />
          </div>
          <OutdoorMap
            :class="[{ show: mapMode == 1 }, { drawing: isDrawing }]"
            ref="map"
            :center="center"
            :zoom="zoom"
            @loaded="onMapLoaded"
            @groupDrawn="groupDrawn"
            @groupEdited="groupEdited"
          ></OutdoorMap>
          <IndoorMap
            :class="{ show: mapMode == 2 }"
            :plan="plan"
            ref="indoorMap"
            :key="selectedNode ? selectedNode.id : 'root'"
          />
          <div v-if="isDrawing" class="helper">
            {{ $t("ap.drawingHelper") }}
          </div>
        </UiCard>
      </div>
    </div>
    <Toolbar>
      <Btn
        :class="[{ 'no-hover': showActions.add }, 'default']"
        @click.native.prevent="toggleActions('add')"
        :disabled="!addIsActive"
        v-can="'beepiz-add-plans'"
      >
        <i class="fa fa-caret-up"></i>
        <span>{{ $t("global.add") }}</span>
        <div
          slot="actions"
          class="button-actions"
          :class="{ show: showActions.add }"
          v-on-clickaway="closeAddActions"
        >
          <ul>
            <li
              :class="{ disabled: !rulesForAdd.plan }"
              @click="showModal('add', 'plan')"
            >
              <i class="fa fa-map fa-fw"></i>
              {{ $t("plans.btns.addActions.plan") }}
            </li>
            <li
              :class="{ disabled: !rulesForAdd.service }"
              @click="showModal('add', 'service')"
            >
              <i class="beepiz-folder"></i>
              {{ $t("plans.btns.addActions.service") }}
            </li>
            <li
              :class="{ disabled: !rulesForAdd.floor }"
              @click="showModal('add', 'floor')"
            >
              <i class="beepiz-stairs fa-fw"></i>
              {{ $t("plans.btns.addActions.floor") }}
            </li>
            <li
              :class="{ disabled: !rulesForAdd.building }"
              @click="showModal('add', 'building')"
            >
              <i class="fa fa-building fa-fw"></i>
              {{ $t("plans.btns.addActions.building") }}
            </li>
            <li
              :class="{ disabled: !rulesForAdd.group }"
              @click="showModal('add', 'group')"
            >
              <i class="beepiz-plan-group fa-fw"></i>
              {{ $t("plans.btns.addActions.group") }}
            </li>
            <li
              :class="{ disabled: !rulesForAdd.site }"
              @click="showModal('add', 'site')"
            >
              <i class="fa fa-globe fa-fw"></i>
              {{ $t("plans.btns.addActions.site") }}
            </li>
          </ul>
        </div>
      </Btn>
      <Btn
        :class="[{ 'no-hover': showActions.options }, 'default']"
        @click.native.prevent="toggleActions('options')"
        :disabled="isRoot"
        v-can="'beepiz-edit-plans || beepiz-delete-plans'"
      >
        <div
          class="button-actions"
          :class="{ show: showActions.options }"
          v-on-clickaway="closeOptionsActions"
        >
          <ul>
            <li
              :class="{ disabled: !selectedNode || !isDeletable }"
              @click="handleDeleteAction"
              v-can="'beepiz-delete-plans'"
            >
              <i class="fa fa-trash fa-fw"></i> {{ $t("global.delete") }}
            </li>
            <li
              :class="{
                disabled: !selectedNode || selectedNode.id == rootNode.id,
              }"
              @click="handleEditAction"
              v-can="'beepiz-edit-plans'"
            >
              <i class="fa fa-pencil fa-fw"></i> {{ $t("global.edit") }}
            </li>
          </ul>
        </div>
        <i class="fa fa-caret-up"></i>
        <span>{{ $t("global.options") }}</span>
      </Btn>
      <Btn :class="['primary pull-right',{hide:isNotAPable}]" v-if="useDvFlagAccess('beepiz-manage-plan-can:update')">
        <router-link :to="routeAPManager" tag="div"><i
            class="fa fa-map-marker"></i><span>{{ $t('plans.btns.APManager') }}</span></router-link>
      </Btn>
    </Toolbar>

    <Modal ref="modalAddSite" :width="'800px'">
      <span slot="header" v-html="$t('plans.titles.add.site')"></span>
      <div slot="content">
        <ModalSite
          ref="modalAddSiteForm"
          @addSite="handleAddSite"
          @editSite="handleEditSite"
          @close="handleClose"
        ></ModalSite>
      </div>
    </Modal>

    <Modal ref="modalAddBuilding" :width="'800px'">
      <span slot="header" v-html="$t('plans.titles.add.building')"></span>
      <div slot="content">
        <ModalBuilding
          ref="modalAddBuildingForm"
          @addBuilding="handleAddBuilding"
          @editBuilding="handleEditBuilding"
          @close="handleClose"
          :site="selectedNode"
        ></ModalBuilding>
      </div>
    </Modal>

    <Modal ref="modalAddGroup" :width="'400px'">
      <span slot="header" v-html="$t('plans.titles.add.group')"></span>
      <div slot="content">
        <ModalGroup
          ref="modalAddGroupForm"
          :site="selectedNode"
          @addGroup="drawGroup"
          @editGroup="handleEditGroup"
          @close="handleClose"
        ></ModalGroup>
      </div>
    </Modal>

    <Modal ref="modalAddFloor" :width="'400px'">
      <span slot="header" v-html="$t('plans.titles.add.floor')"></span>
      <div slot="content">
        <ModalFloor
          ref="modalAddFloorForm"
          :site="selectedNode"
          @addFloor="handleAddFloor"
          @editFloor="handleEditFloor"
          @close="handleClose"
        ></ModalFloor>
      </div>
    </Modal>

    <Modal ref="modalAddService" :width="'400px'">
      <span slot="header" v-html="$t('plans.titles.add.service')"></span>
      <div slot="content">
        <ModalService
          ref="modalAddServiceForm"
          :site="selectedNode"
          @addService="handleAddService"
          @editService="handleEditService"
          @close="handleClose"
        ></ModalService>
      </div>
    </Modal>

    <Modal ref="modalAddPlan" :width="'400px'" :height="'100%'">
      <span slot="header" v-html="$t('plans.titles.add.plan')"></span>
      <div slot="content">
        <ModalPlan
          ref="modalAddPlanForm"
          :site="selectedNode"
          @addPlan="handleAddPlan"
          @editPlan="handleEditPlan"
          @close="handleClose"
        ></ModalPlan>
      </div>
    </Modal>

    <div
      class="context-menu"
      id="ctm"
      v-can="'beepiz-add-plans'"
      :class="{ show: showCtm }"
      :style="contextMenuPosition"
      v-on-clickaway="hideCtm"
    >
      <ul>
        <li
          :class="{ disabled: !rulesForAdd.plan }"
          @click="showModal('add', 'plan')"
        >
          <i class="fa fa-map fa-fw"></i> {{ $t("plans.btns.addActions.plan") }}
        </li>
        <li
          :class="{ disabled: !rulesForAdd.service }"
          @click="showModal('add', 'service')"
        >
          <i class="fa fa-folder fa-fw"></i>
          {{ $t("plans.btns.addActions.service") }}
        </li>
        <li
          :class="{ disabled: !rulesForAdd.floor }"
          @click="showModal('add', 'floor')"
        >
          <i class="fa fa-bars fa-fw"></i>
          {{ $t("plans.btns.addActions.floor") }}
        </li>
        <li
          :class="{ disabled: !rulesForAdd.building }"
          @click="showModal('add', 'building')"
        >
          <i class="fa fa-building fa-fw"></i>
          {{ $t("plans.btns.addActions.building") }}
        </li>
        <li
          :class="{ disabled: !rulesForAdd.group }"
          @click="showModal('add', 'group')"
        >
          <i class="fa fa-folder fa-fw"></i>
          {{ $t("plans.btns.addActions.group") }}
        </li>
        <li
          :class="{ disabled: !rulesForAdd.site }"
          @click="showModal('add', 'site')"
        >
          <i class="fa fa-globe fa-fw"></i>
          {{ $t("plans.btns.addActions.site") }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import API from "@/services/API.js";

import Rules from "@/services/plansRules";
import Utils from "@/services/utils";

import Titlebar from "@/components/ui/Titlebar";
import Toolbar from "@/components/ui/Toolbar";
import Btn from "@/components/ui/Button";
import UiCard from "@/components/ui/Card";

import Tree from "@/components/ui/tree/Tree";
import OutdoorMap from "@/components/plans/map/OutdoorMap";
import IndoorMap from "@/components/plans/map/IndoorMap";

import Modal from "@/components/ui/Modal";
import ModalSite from "@/components/plans/modals/ModalSite";
import ModalBuilding from "@/components/plans/modals/ModalBuilding";
import ModalGroup from "@/components/plans/modals/ModalGroup";
import ModalFloor from "@/components/plans/modals/ModalFloor";
import ModalService from "@/components/plans/modals/ModalService";
import ModalPlan from "@/components/plans/modals/ModalPlan";

import Icons from "@/services/iconMapping.js";

import LoadingMini from "@/components/ui/LoadingMini";

import state from "@/services/PlanTreeState";

import { mixin as clickaway } from "vue-clickaway";

import indexOf from "lodash/indexOf";
import UiBrandLogoWide from "@/components/ui/BrandLogoWide";
import {useDvFlag, useDvFlagAccess} from "../../services/featureFlag";

export default {
  components: {
    Titlebar,
    Toolbar,
    Btn,
    UiCard,
    Tree,
    OutdoorMap,
    IndoorMap,
    Modal,
    ModalSite,
    ModalBuilding,
    ModalGroup,
    ModalFloor,
    ModalService,
    ModalPlan,
    LoadingMini,
    UiBrandLogoWide,
  },
  mixins: [clickaway],
  name: "plans",
  data() {
    return {
      showActions: {
        add: false,
        options: false,
      },
      mapMode: 0, //0: Aucune, 1: Outdoor, 2: Indoor
      rulesForAdd: {
        site: false,
        group: false,
        building: false,
        floor: false,
        plan: false,
      },
      center: null,
      zoom: null,
      options: {
        sortable: true,
        keepActivate: true,
        constrain: true,
      },
      selectedNode: null,
      externalSelected: null,
      rootNode: null,
      tempCreatedGroup: null,
      isDrawing: false,
      hasDrawn: false,
      ctmPos: {
        x: 0,
        y: 0,
        originY: "top",
      },
      showCtm: false,
      treeData: [],
      addFirstTime: true,
      optionsFirstTime: true,
    };
  },
  computed: {
    // logo: function () {
    //     if (Utils.isThemeSfr()) return srcSFRDati
    //     if (Utils.isThemeCatsafe()) return srcSFRDati
    //     return srcBlack
    // },
    routeAPManager: function () {
      if (this.selectedNode != null && this.selectedNode.type != 1) {
        return "/plans/actionpoints/manager/" + this.selectedNode.id;
      }
      return "/plans/actionpoints/manager";
    },
    plan: function () {
      if (this.selectedNode == null || this.selectedNode.plan == null) {
        return null;
      } else {
        var planObj = {
          width: this.selectedNode.plan.width,
          height: this.selectedNode.plan.height,
        };
        planObj.data =
          API.apiBaseURL +
          API.route(API.endpoints.plans.getImage, {
            planId: this.selectedNode.id,
          });
        return planObj;
      }
    },
    addIsActive: function () {
      if (this.rulesForAdd != null) {
        return (
          this.rulesForAdd.site ||
          this.rulesForAdd.group ||
          this.rulesForAdd.building ||
          this.rulesForAdd.floor ||
          this.rulesForAdd.service ||
          this.rulesForAdd.plan
        );
      }
      return false;
    },
    contextMenuPosition: function () {
      return (
        "left:" +
        this.ctmPos.x +
        "px;top:" +
        this.ctmPos.y +
        "px;transform-origin:left " +
        this.ctmPos.originY
      );
    },
    isRoot: function () {
      if (this.selectedNode) {
        return this.selectedNode.id == this.rootNode.id;
      }
      return true;
    },
    isDeletable: function () {
      if (this.selectedNode.children && this.selectedNode.children.length > 0) {
        return false;
      }
      return true;
    },
    isNotAPable: function () {
      if (this.selectedNode != null) {
        return indexOf([2, 3, 4, 7], this.selectedNode.type) == -1;
      }
      return true;
    },
    icons: function () {
      return Icons.plans;
    },
  },

  methods: {
    useDvFlagAccess,
    onMapLoaded: function () {
      if (useDvFlagAccess("beepiz-manage-plan-can:read")) {
        this.loadTree();
        window.Vfc.$refs.loading.hide();
      }
    },
    handleNode: function (node) {
      this.$refs.map.map.clearFeatures();
      this.$refs.map.map.removeHTMLMarkers();
      this.showCtm = false;
      this.showActions.add = false;
      this.showActions.options = false;
      this.$refs.indoorMap.map.clearFeatures();
      if (this.hasDrawn) {
        this.$refs.map.clearDraw();
        this.hasDrawn = false;
      }

      if (
        (this.selectedNode != null && this.selectedNode.id == node.id) ||
        node.type == 1
      ) {
        this.mapMode = 0;
        this.selectedNode = this.rootNode;
        this.$refs.tree.unactivateNode(node);
        this.rulesForAdd = Rules.getRules("add", 1);
        this.$refs.map.reset();
      } else {
        this.selectedNode = node;
        if (this.mapMode == 1) {
          this.loadAP();
        }

        this.rulesForAdd = Rules.getRules("add", node.type);
        if (node.polygon != null) {
          this.$refs.map.showGroup(node);
        } else if (node.where != null && node.where.point != null) {
          if (node.where.point.lng != null) {
            this.center = [node.where.point.lng, node.where.point.lat];
            this.$refs.map.map.addHTMLMarker(
              {
                type: "html",
                value:
                  '<i class="beepiz-marker"></i><i class="plan-entity-icon ' +
                  this.icons[node.type] +
                  '"></i>',
              },
              this.center[1],
              this.center[0],
              "marker-plan-entity"
            );
          }
          if (node.zoom != null) {
            this.zoom = node.zoom;
          }
        } else {
          var building = Utils.getBuildingParent(this.treeData, node);
          if (building.where.point.lng != null) {
            this.center = [building.where.point.lng, building.where.point.lat];
            this.$refs.map.map.addHTMLMarker(
              {
                type: "html",
                value:
                  '<i class="beepiz-marker"></i><i class="plan-entity-icon ' +
                  this.icons[building.type] +
                  '"></i>',
              },
              this.center[1],
              this.center[0],
              "marker-plan-entity"
            );
          }
          if (building.zoom != null) {
            this.zoom = building.zoom;
          }
        }
      }

      switch (this.selectedNode.type) {
        case 7:
          this.loadPlan(this.selectedNode.id);
          break;
        case 6:
          this.mapMode = 0;
          break;
        case 5:
          this.mapMode = 0;
          break;
        case 4:
          this.mapMode = 1;
          break;
        case 3:
          this.hasDrawn = true;
          this.mapMode = 1;
          break;
        case 2:
          this.mapMode = 1;
          break;
      }
    },
    handleRightClick: function (evt, node) {
      if (this.selectedNode != node) {
        this.$refs.tree.unactivateNodes(node);
        node.active = true;
        this.handleNode(node);
      }
      this.showCtm = true;
      var availableHeight = window.innerHeight - evt.pageY;
      var ctmHeight = document.getElementById("ctm").offsetHeight;
      if (availableHeight < ctmHeight) {
        this.ctmPos.y = evt.pageY - ctmHeight;
        this.ctmPos.originY = "bottom";
      } else {
        this.ctmPos.y = evt.pageY;
        this.ctmPos.originY;
      }
      this.ctmPos.x = evt.pageX - 80;
    },
    handleSort: function (evt) {
      var orders = [];
      var id = evt.target.getAttribute("data-id");
      var node = {};
      if (id == this.rootNode.id) {
        node = this.rootNode;
      } else {
        node = Utils.getByIdInTree(
          this.treeData,
          evt.target.getAttribute("data-id")
        );
      }
      if (node != null) {
        for (var key in node.children) {
          orders.push(node.children[key].id);
        }
        axios
          .put(API.route(API.endpoints.plans.order, { planId: node.id }), {
            brotherOrder: orders.join(","),
          })
          .then((response) => {})
          .catch((error) => {});
      }
    },
    handleEditAction: function () {
      this.showModal("edit", Rules.types[this.selectedNode.type]);
    },
    handleEditSite: function (obj) {
      delete obj.plan;
      delete obj.scale;
      this.handleEdit("site", obj);
    },
    handleEditGroup: function (obj, groupEdit) {
      delete obj.plan;
      delete obj.scale;
      if (groupEdit) {
        this.$refs.map.map.clearFeatures();
        this.editGroup(obj);
      } else {
        this.handleEdit("group", obj);
      }
    },
    handleEditBuilding: function (obj) {
      delete obj.plan;
      delete obj.scale;
      this.handleEdit("building", obj);
    },
    handleEditFloor: function (obj) {
      this.handleEdit("floor", obj);
    },
    handleEditService: function (obj) {
      this.handleEdit("service", obj);
    },
    handleEditPlan: function (obj) {
      this.handleEdit("plan", obj);
    },
    handleEdit: function (type, obj) {
      var vThis = this;
      var objToEdit = JSON.parse(JSON.stringify(obj));

      if (type == "plan" && objToEdit.plan) {
        objToEdit.plan = objToEdit.plan.data;
      }

      if (
        (objToEdit.children && objToEdit.children.length > 0) ||
        objToEdit.hasAPs
      ) {
        delete objToEdit.where;
        delete objToEdit.zoom;
      }

      if (objToEdit.plan == null) delete objToEdit.plan;
      if (objToEdit.scale == null) delete objToEdit.scale;

      delete objToEdit.id;
      delete objToEdit.brotherOrder;
      delete objToEdit.parentId;
      delete objToEdit.pathOrder;
      delete objToEdit.active;
      delete objToEdit.hasAPs;
      delete objToEdit.opened;
      delete objToEdit.type;
      delete objToEdit.children;
      delete objToEdit.polygon;

      /*objToEdit.name = obj.name;
      if(obj.scale != null){
          objToEdit.scale = obj.scale;
      }*/
      axios
        .put(API.route(API.endpoints.plans.edit, { planId: obj.id }), objToEdit)
        .then((response) => {
          vThis.$refs["modalAdd" + Utils.capitalize(type) + "Form"].resetForm();
          vThis.$refs["modalAdd" + Utils.capitalize(type)].hide();
          vThis.selectedNode = vThis.rootNode;
          vThis.loadTree(obj.id);
        })
        .catch((error) => {});
    },
    handleDeleteAction: function () {
      var vThis = this;
      if (!this.isDeletable) {
        return false;
      }

      var idToDelete = vThis.selectedNode.id;

      window.Vfc.$refs.confirm.show({
        title: Utils.t("global.deleteModalTitle"),
        content: Utils.t("global.deleteModalContent"),
        callbackYes: function () {
          axios
            .delete(
              API.route(API.endpoints.plans.delete, {
                planId: vThis.selectedNode.id,
              })
            )
            .then((response) => {
              vThis.$refs.map.clearDraw();
              vThis.handleNode(vThis.rootNode);
              if (
                vThis.$route.params.planid &&
                vThis.$route.params.planid == idToDelete
              ) {
                window.V._router.replace("/plans/manager", () => {
                  vThis.loadTree();
                });
              } else {
                vThis.loadTree();
              }
            })
            .catch((error) => {});
        },
      });
    },
    toggleActions: function (key, force) {
      if (force != null) {
        this.showActions[key] = force;
      } else {
        this.showActions[key] = !this.showActions[key];
      }
      for (var keyShows in this.showActions) {
        if (keyShows != key) {
          this.showActions[keyShows] = false;
          this[key + "FirstTime"] = true;
        }
      }
    },
    closeAddActions: function () {
      if (this.showActions["add"] && !this.addFirstTime) {
        this.showActions["add"] = false;
        this.addFirstTime = true;
      } else {
        this.addFirstTime = false;
      }
    },
    closeOptionsActions: function () {
      if (this.showActions["options"] && !this.optionsFirstTime) {
        this.showActions["options"] = false;
        this.optionsFirstTime = true;
      } else {
        this.optionsFirstTime = false;
      }
    },
    showModal: function (action, type) {
      if (
        action == "add" &&
        this["rulesFor" + Utils.capitalize(action)][type]
      ) {
        this.showCtm = false;
        this.$refs[
          "modal" + Utils.capitalize(action) + Utils.capitalize(type) + "Form"
        ].edit(false);
        this.$refs[
          "modal" + Utils.capitalize(action) + Utils.capitalize(type)
        ].show();
      } else if (action == "edit") {
        this.$refs["modalAdd" + Utils.capitalize(type) + "Form"].edit(
          true,
          this.selectedNode
        );
        this.$refs["modalAdd" + Utils.capitalize(type)].show();
      }
    },
    hideModal: function (action, type) {
      this.$refs[
        "modal" + Utils.capitalize(action) + Utils.capitalize(type)
      ].hide();
    },
    hideCtm: function () {
      this.showCtm = false;
    },
    editGroup: function (item) {
      this.isDrawing = true;
      this.hideModal("add", "group");
      this.tempCreatedGroup = item;
      this.$refs.map.drawEditMode(true, item);
    },
    groupEdited: function (drawnItem) {
      this.isDrawing = false;
      var obj = {};
      obj.id = this.tempCreatedGroup.id;
      obj.name = this.tempCreatedGroup.label;
      obj.polygon = JSON.stringify(drawnItem.features[0].geometry.coordinates);
      //this.handleEdit('group', obj);
    },
    drawGroup: function (item) {
      this.isDrawing = true;
      this.hideModal("add", "group");
      this.tempCreatedGroup = item;
      this.$refs.map.drawMode(true);
    },
    groupDrawn: function (drawnItem) {
      this.isDrawing = false;
      var obj = {};
      obj.name = this.tempCreatedGroup.label;
      obj.type = 3;
      obj.polygon = JSON.stringify(drawnItem.features[0].geometry.coordinates);
      obj.parentId = this.selectedNode.id;
      this.addPlanEntity("group", obj);
    },
    selectItem: function (idToSelect) {
      var nodeToSelect = Utils.getByIdInTree(
        this.rootNode.children,
        idToSelect
      );
      if (nodeToSelect !== false) {
        var path = nodeToSelect.pathOrder.split(".");
        var node = null;
        for (var i = 1; i < path.length; i++) {
          node = Utils.getByIdInTree(this.rootNode.children, path[i]);
          state.opened[node.id] = true;
          state.active = nodeToSelect.id;
        }
        this.handleNode(nodeToSelect);
      }
      return nodeToSelect;
    },
    loadTree: function (idToSelect) {
      this.$refs.loading.show();
      axios.get(API.endpoints.plans.index).then((response) => {
        this.rootNode = response.data;
        this.treeData = response.data.children;
        if (idToSelect) {
          var node = this.selectItem(idToSelect);
          this.$refs.loading.hide();
        } else if (this.$route.params.planid) {
          var node = this.selectItem(this.$route.params.planid);
          if (node.type == 7) {
            this.loadPlan(node.id);
          } else {
            this.$refs.loading.hide();
          }
        } else {
          this.selectedNode = this.rootNode;
          this.$refs.loading.hide();
        }
      });
    },
    loadPlan: function (idPlan) {
      this.$refs.loading.show();
      axios
        .get(API.route(API.endpoints.plans.show, { planId: idPlan }))
        .then((response) => {
          if (response.data.plan) {
            this.selectedNode.plan = response.data.plan;
            this.mapMode = 2;
            this.loadAP();
          }
          this.$refs.loading.hide();
        });
    },
    loadAP: function () {
      axios
        .get(
          API.route(API.endpoints.plansActionpoints.index + "?withimage=1", {
            planId: this.selectedNode.id,
          }),
          { headers: { Accept: "application/vnd.geo+json" } }
        )
        .then((response) => {
          var temp = response.data;
          temp.features = Utils.addIndexGeojson(temp.features);
          this.selectedNode.hasAPs = temp.features.length > 0 ? true : false;
          if (this.mapMode == 2) {
            this.$refs.indoorMap.map.addGeoJson(temp, true);
            this.$refs.indoorMap.map.addPopup(true);
          } else {
            this.$refs.map.map.addGeoJson(temp, true);
            this.$refs.map.map.addPopup(true);

            if (this.selectedNode.polygon != null) {
              this.$refs.map.showGroup(this.selectedNode);
            } else {
              if (
                this.selectedNode.where != null &&
                this.selectedNode.where.point != null
              ) {
                if (this.selectedNode.where.point.lng != null) {
                  this.center = [
                    this.selectedNode.where.point.lng,
                    this.selectedNode.where.point.lat,
                  ];
                }
                if (this.selectedNode.zoom != null) {
                  this.zoom = this.selectedNode.zoom;
                }
              }
            }
          }
        });
    },
    handleAddSite: function (obj) {
      obj.parentId = this.selectedNode.id;
      this.addPlanEntity("site", obj);
    },
    handleAddBuilding: function (obj) {
      obj.parentId = this.selectedNode.id;
      this.addPlanEntity("building", obj);
    },
    handleAddFloor: function (obj) {
      obj.parentId = this.selectedNode.id;
      this.addPlanEntity("floor", obj);
    },
    handleAddService: function (obj) {
      obj.parentId = this.selectedNode.id;
      this.addPlanEntity("service", obj);
    },
    handleAddPlan: function (obj) {
      obj.parentId = this.selectedNode.id;
      this.addPlanEntity("plan", obj);
    },
    handleClose: function (action, type) {
      this.$refs["modalAdd" + Utils.capitalize(type) + "Form"].resetForm();
      this.hideModal(action, type);
    },
    addPlanEntity: function (type, obj) {
      var vThis = this;

      axios
        .post(API.endpoints.plans.add, obj)
        .then((response) => {
          vThis.$refs["modalAdd" + Utils.capitalize(type) + "Form"].resetForm();
          vThis.$refs["modalAdd" + Utils.capitalize(type)].hide();
          vThis.loadTree(response.data.message);
        })
        .catch((error) => {
          var formRef =
            vThis.$refs["modalAdd" + Utils.capitalize(type) + "Form"];
          if (formRef && typeof formRef.errorForm === "function") {
            formRef.errorForm(error);
          }
        });
    },
  },
  beforeCreate: function () {
    this.rulesForAdd = Rules.getRules("add", 1);
  },
  mounted: function () {
    const flag = useDvFlag('beepiz-manage-plan-can:read');
    if (!flag.access && flag.fallback==="no_plan") {
      this.$router.push({ name: 'AccessDenied', params: { slug: "plans"} })
    }
    else if (!flag.access && flag.fallback==="no_role") {
      this.$router.push({ name: 'AccessDenied', params: { slug: "plans_no_role"} })
    }else {
      var vThis = this;
      this.rulesForAdd = Rules.getRules("add", 1);
    }
  },
};
</script>

<style lang="scss" scoped></style>
